import type { ICommand } from '../../../core/commands/ICommand';
import { getNodeMutationManager } from '../../../core/commands/mutations/NodeMutationManager';
import type { Node } from '../../../core/models/scene';
import { useDiagramStore } from '../../../stores/diagramStore';
import { validateShapeLimitOrPromptUpgrade } from '../../../utils/shapeLimitUtils';
import { getShapeManifest } from '../../../lib/diagram/registries';
import type { IScenarioRecordable, CommandDeltaContribution } from './IScenarioRecordable';
import {
  getOrgChartDefaultPersonName,
  getOrgChartDefaultRole,
} from '../models/defaults';
import { createNodeId, type OrgNodeType } from '../models/Scenario';
import {
  DEFAULT_ORG_PERSON_SHAPE_TYPE,
  isOrgPersonNodeType,
} from '../models/defaults';
import { createNewRecordBinding } from '../../data-binding/utils/createNewRecordBinding';
import { snapPointToGrid } from '../../../lib/diagram/gridSnap';

export interface CreateOrgPersonParams {
  id?: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  personId?: string;
  name?: string;
  role?: string;
  email?: string;
  joinDate?: string;
  accentColor?: string;
  shapeType?: 'photo' | 'text';
  /**
   * Optional: exact node type for the new person (e.g. org_person_photo_inner_left).
   * When set, overrides shapeType and uses that type's default dimensions.
   */
  nodeType?: string;
  /** Optional: data source ID for auto-creating repository binding */
  dataSourceId?: string;
  /** Optional: user ID for tracking who created the record */
  userId?: string;
}

export class CreateOrgPersonCommand implements ICommand, IScenarioRecordable {
  id: string;
  name = 'Create Person';
  description: string;
  timestamp = Date.now();
  readonly isScenarioRecordable = true as const;

  private node: Node;
  private createdBinding?: ReturnType<typeof createNewRecordBinding>;

  constructor(params: CreateOrgPersonParams) {
    this.id = `create-org-person-${Date.now()}`;
    const defaultPersonName = getOrgChartDefaultPersonName();
    const defaultRole = getOrgChartDefaultRole();
    this.description = `Create person: ${params.name || defaultPersonName}`;
    const nodeId = params.id ?? `org-person-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const shapeType = params.shapeType ?? DEFAULT_ORG_PERSON_SHAPE_TYPE;
    const resolvedNodeType =
      params.nodeType && isOrgPersonNodeType(params.nodeType)
        ? params.nodeType
        : shapeType === 'photo'
          ? 'org_person_photo'
          : 'org_person_text';
    const manifest = getShapeManifest('org', resolvedNodeType);
    const defaultDims = manifest?.defaultDimensions || { width: 270, height: 80 };
    const width = params.width ?? defaultDims.width;
    const height = params.height ?? defaultDims.height;
    const snappedPosition = snapPointToGrid({ x: params.x, y: params.y });

    const attrs: Record<string, unknown> = {
      personId: params.personId || '',
      name: params.name || defaultPersonName,
      role: params.role ?? defaultRole,
      email: params.email,
      joinDate: params.joinDate,
      accentColor: params.accentColor ?? '#10B981',
      authoredPlacement: true,
    };

    if (params.dataSourceId) {
      const initialData: Record<string, unknown> = {
        name: params.name || defaultPersonName,
        title: params.role ?? defaultRole,
      };
      if (params.email) initialData.email = params.email;

      this.createdBinding = createNewRecordBinding({
        shapeId: nodeId,
        sourceId: params.dataSourceId,
        userId: params.userId ?? 'unknown',
        initialData,
      });
    }

    this.node = {
      id: nodeId,
      type: resolvedNodeType,
      x: snappedPosition.x,
      y: snappedPosition.y,
      w: width,
      h: height,
      width,
      height,
      category: 'orgchart',
      name: params.name || defaultPersonName,
      attrs,
      props: this.createdBinding ? { dataBinding: this.createdBinding } : undefined,
    } as Node;
  }

  getNode(): Node {
    return this.node;
  }

  execute(): void {
    const storeSnapshot = useDiagramStore.getState();
    const isNew = !storeSnapshot.nodes.some(n => n.id === this.node.id);

    if (isNew && !validateShapeLimitOrPromptUpgrade(storeSnapshot)) {
      return;
    }

    const mutationManager = getNodeMutationManager();
    mutationManager.addNode(this.node);
  }

  getCreatedId(): string {
    return this.node.id;
  }

  getCreatedBinding(): ReturnType<typeof createNewRecordBinding> | undefined {
    return this.createdBinding;
  }

  undo(): void {
    const mutationManager = getNodeMutationManager();
    mutationManager.deleteNode(this.node.id);
    this.createdBinding = undefined;
  }

  redo(): void {
    this.execute();
  }

  toScenarioDelta(): CommandDeltaContribution {
    const attrs = this.node.attrs as Record<string, unknown>;
    return {
      additions: [
        {
          id: createNodeId(this.node.id),
          type: this.node.type as OrgNodeType,
          name: (attrs?.name as string) ?? 'Unknown',
          title: attrs?.role as string | undefined,
          position: { x: this.node.x, y: this.node.y },
          props: {
            email: attrs?.email as string | undefined,
            role: attrs?.role as string | undefined,
          },
        },
      ],
    };
  }

  getUndoDelta(): CommandDeltaContribution {
    const attrs = this.node.attrs as Record<string, unknown>;
    return {
      removals: [
        {
          nodeId: createNodeId(this.node.id),
          originalData: {
            name: (attrs?.name as string) ?? 'Unknown',
            title: attrs?.role as string | undefined,
            type: this.node.type as OrgNodeType,
            props: {
              email: attrs?.email as string | undefined,
              role: attrs?.role as string | undefined,
            },
          },
        },
      ],
    };
  }
}
